import { useApolloClient } from "@apollo/client";
import { assert, notNull } from "@msys/common";
import { TFunction } from "@msys/tolgee";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  GridGroupingColDefOverride,
  GridRowId,
  ListHeader,
  ModalOpenButton,
  useDataGridGroupExpandedStore,
  VisibilityIndicatorIcon,
} from "@msys/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridCallbackDetails,
  GridCellParams,
  gridClasses,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
  GridTreeDataGroupingCell,
  isAutogeneratedRow,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { search, sortKind } from "fast-fuzzy";
import {
  Form,
  Formik,
  FormikHelpers,
  useField,
  useFormikContext,
} from "formik";
import {
  capitalize,
  difference,
  groupBy,
  isEqual,
  pick,
  uniqueId,
} from "lodash-es";
import { useSnackbar } from "notistack";
import omitDeep from "omit-deep-lodash";
import React, { Dispatch, SetStateAction } from "react";
import { useLatest } from "react-use";
import * as Yup from "yup";
import {
  DocType,
  EnterItemProps2ValueEntry,
  PermissionName,
  Props2,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import type { DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { useUserData } from "../../auth/useUserData.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import {
  EditMode,
  SwitchEditModeButton,
} from "../../commons/button/SwitchEditModeButton.js";
import { FilterChipGroup } from "../../commons/filters/FilterChip.js";
import { AutocompleteField } from "../../commons/form-fields/AutocompleteField.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { ManualSave } from "../../commons/form-fields/ManualSave.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { useStateWithUrlParams } from "../../commons/hooks/useStateWithUrlParams.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { highlightWithMatches } from "../../utils.js";
import { usePropertiesSection_EnterItemProps2ValueMutation } from "../doc-items/boxes/PropertiesSection.generated.js";
import { ButtonSelectItemsMultiple } from "../doc-items/ButtonSelectItemsMultiple.js";
import { AskIfConditionExpressionIconField } from "../doc-items/fields/AskIfConditionExpressionField.js";
import { ItemSelect_ItemFragment } from "../doc-items/ItemSelect.generated.js";
import { formatItem } from "../doc-items/ItemSelect.js";
import {
  defaultGroupValues,
  GroupModal,
} from "../doc-items/modals/GroupModal.js";
import {
  createPropertyEntry as createPropertyEntryOnAdd,
  ItemPropertyAddModalWithItemSelect,
} from "../doc-items/modals/PropertyAddModal.js";
import { PropertyAllowedValuesModal } from "../doc-items/modals/PropertyAllowedValuesModal.js";
import {
  createPropertyEntry as createPropertyEntryOnEdit,
  FormValues as PropertyEditModalFormValues,
  getInitialValues,
  isRangeDefined,
} from "../doc-items/modals/PropertyEditModal.js";
import { getInitialValuesWithTemplateType } from "../doc-items/modals/PropertyEditWithTemplateTypeModal.js";
import { PropertyFromTemplateTypeAllowedValuesModal } from "../doc-items/modals/PropertyFromTemplateTypeAllowedValuesModal.js";
import { QuestionControlModal } from "../doc-items/modals/QuestionControlModal.js";
import {
  getPropertyType,
  getPropInputEntryKey,
  getPropValues,
  getPropValuesFromType,
  getTemplateTypePropValues,
  groupProperties,
  isComputedProp,
  PROP_VALUES,
  PropType,
  PropValues,
  TemplateTypePropValues,
  TypeFields,
  ungroupProperties,
} from "../doc-items/properties.js";
import { PropertyField } from "../doc-items/PropertyField.js";
import { defaultQuestionControlValues } from "../doc-items/QuestionControlSection.js";
import { usePropertyHelpers } from "../doc-items/usePropertyHelpers.js";
import { usePropertyUnits } from "../doc-items/usePropertyUnits.js";
import {
  getQuestionControlLabel,
  useQuestionContol,
} from "../doc-items/useQuestionContol.js";
import { useDataGridStateStore } from "../users/useDataGridStateStore.js";
import {
  ManagePropertiesTable_ItemFragment,
  useDefineItemProps2BulkMutation,
} from "./ManagePropertiesTable.generated";

interface Props {
  docType: DocType;
  documentViewerPermissions: PermissionName[];
  items: ManagePropertiesTable_ItemFragment[];
  templateTypeProperties?: Props2[];
  projectId: string | null;
  docId: string;
  topContent?: React.ReactNode;
}

declare const __propKey: unique symbol;
type PropKey = string & { readonly [__propKey]: true };

type PropertyFormValue = PropertyEditModalFormValues &
  PropValues & {
    isComputed: boolean;
    key: string;
    type: PropType;
    item: Omit<ManagePropertiesTable_ItemFragment, "props2">;
    deleted: boolean;
  };

type PropertyGridValue = PropValues &
  TypeFields & {
    key: string;
    item: { id: string };
    prop: Props2 | null;
    created: boolean;
    deleted: boolean;
    isComputed: boolean;
    templateTypePropValues?: TemplateTypePropValues;
    match?: { index: number; length: number }[];
  };

type FormValues = Record<string, PropertyFormValue>;

export const getKey = (prop: {
  key: string;
  item: { id: string };
}): PropKey => {
  return `item:${prop.item.id}/prop:${prop.key}` as PropKey;
};

type Filters = {
  itemIds: string[];
  searchTerm: string;
};

const defaultFilters: Filters = { itemIds: [], searchTerm: "" };

const cleanFormValues = (values: PropertyFormValue) => {
  // step 1: clean attachments
  let _values: any = {
    ...values,
    allowedValues: values.allowedValues.map(av => ({
      ...av,
      media: av.media ? pick(av.media, "url", "title", "mimeType") : av.media,
    })),
    valuesMediaBool: values.valuesMediaBool.map(vm => ({
      ...vm,
      media: vm.media ? pick(vm.media, "url", "title", "mimeType") : vm.media,
    })),
  };

  // step 2: pick only specific fields
  const fieldsToCompare = [
    "defineAllowedValues",
    "allowedValues",
    "defineValuesMediaBool",
    "valuesMediaBool",
    "questionControl",
    "prompt",
    "setRange",
    "setStep",
    "range",
    "step",
    "label",
    "unit",
    "essential",
    "group",
    "clientVisibility",
    "askIfExpr",
    "customizable",
  ];
  _values = pick(_values, ...fieldsToCompare);

  // step 3: clean graphql __typename
  _values = omitDeep(_values, ["__typename"]);

  return _values;
};

const isEqualFormValues = (
  values1: PropertyFormValue,
  values2: PropertyFormValue
) => {
  return isEqual(cleanFormValues(values1), cleanFormValues(values2));
};

export const ManagePropertiesTable = ({
  docType,
  documentViewerPermissions,
  items,
  templateTypeProperties,
  projectId,
  docId,
  topContent,
  pageProps,
}: Props & { pageProps: React.ComponentProps<typeof Page> }) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { enqueueSnackbar } = useSnackbar();
  const { getUnits } = usePropertyUnits();
  const unitsOptions = React.useMemo(() => getUnits(), [getUnits]);
  const { getBoolLabel } = usePropertyHelpers();
  const viewer = useUserData().currentUser!;

  const document: DocumentByType = React.useMemo(
    () => ({
      docType,
      viewerPermissions: documentViewerPermissions,
    }),
    [docType, documentViewerPermissions]
  );

  const theme = useTheme();

  const gridApiRef = useGridApiRef();

  const [editMode, setEditMode] = React.useState<EditMode>("normal");

  const [selectedProperties, setSelectedProperties] =
    React.useState<GridRowSelectionModel>(defaultSelectionModel);

  const latestSelectedProperties = useLatest(selectedProperties);

  const [filters, setFilters] = useStateWithUrlParams<Filters>(
    "filters",
    defaultFilters
  );
  const isFilterActive = Boolean(
    filters.searchTerm || filters.itemIds.length > 0
  );
  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const { isGroupExpandedByDefault, onStateChange } =
    useDataGridGroupExpandedStore(
      gridApiRef,
      `${docType}-${projectId || "null"}-${docId}`,
      {
        keepNonExistingKeys: isFilterActive,
      }
    );

  const props = React.useMemo(
    () =>
      items.flatMap(({ props2, ...item }) =>
        // sort according to groups
        ungroupProperties(groupProperties(props2)).map(property => ({
          ...property,
          item,
        }))
      ),
    [items]
  );

  const stateStore = useDataGridStateStore("ManagePropertiesTable", {
    rowGrouping: { model: ["item"] },
  });

  const client = useApolloClient();
  const [defineItemProps2Bulk] = useDefineItemProps2BulkMutation({
    client,
  });

  const [enterItemProps2] = usePropertiesSection_EnterItemProps2ValueMutation({
    client,
  });
  const setPropertyValue = React.useCallback(
    async (itemId: string, property: EnterItemProps2ValueEntry) => {
      await enterItemProps2({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            entries: [property],
          },
        },
      });
    },
    [docId, enterItemProps2, projectId]
  );

  const initialValues = React.useMemo(
    (): FormValues =>
      Object.fromEntries(
        props.map((prop, index) => {
          const templateTypeProperty =
            templateTypeProperties && prop.item.isRootItem
              ? (templateTypeProperties.find(
                  ttProp => ttProp.key === prop.key
                ) ?? null)
              : null;

          return [
            getKey(prop),
            {
              ...(templateTypeProperty
                ? getInitialValuesWithTemplateType(
                    prop,
                    templateTypeProperty,
                    unitsOptions
                  )
                : getInitialValues(prop, unitsOptions)),
              ...getPropValues(prop),
              key: prop.key,
              type: getPropertyType(prop),
              isComputed: isComputedProp(prop),
              item: prop.item,
              deleted: false,
              ...(templateTypeProperty
                ? {
                    templateTypePropValues: getTemplateTypePropValues(
                      templateTypeProperty,
                      prop
                    ),
                  }
                : undefined),
            } as PropertyFormValue,
          ];
        })
      ),
    [props, templateTypeProperties, unitsOptions]
  );

  const initialRows = React.useMemo(
    (): PropertyGridValue[] =>
      props.map((prop, index) => {
        const templateTypeProperty =
          templateTypeProperties && prop.item.isRootItem
            ? (templateTypeProperties.find(ttProp => ttProp.key === prop.key) ??
              null)
            : null;
        return {
          ...getPropValues(prop),
          key: prop.key,
          type: getPropertyType(prop),
          isComputed: isComputedProp(prop),
          item: { id: prop.item.id },
          prop,
          created: false,
          deleted: false,
          ...(templateTypeProperty
            ? {
                templateTypePropValues: getTemplateTypePropValues(
                  templateTypeProperty,
                  prop
                ),
              }
            : undefined),
        };
      }),
    [props, templateTypeProperties]
  );

  const [rows, setRows] = React.useState<PropertyGridValue[]>([]);

  // reset form on items changes from server
  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const filteredRows = React.useMemo(() => {
    let result = rows;

    if (filters.itemIds && filters.itemIds.length > 0) {
      result = result.filter(r => filters.itemIds.includes(r.item.id));
    }

    if (filters.searchTerm) {
      const searchResultRows = search(filters.searchTerm, result, {
        keySelector: row => row.key,
        threshold: 0.7,
        returnMatchData: true,
        sortBy: sortKind.insertOrder,
      });

      result = searchResultRows.map(r => ({
        ...r.item,
        match: r.match ? [r.match] : undefined,
      }));
    }

    return result;
  }, [filters.itemIds, filters.searchTerm, rows]);

  const onRowSelectionModelChange = React.useCallback(
    (
      rowSelectionModel: GridRowSelectionModel,
      details: GridCallbackDetails<any>
    ) => {
      // expand selected group if it wasn't expanded before
      difference(
        Array.from(rowSelectionModel.ids),
        Array.from(latestSelectedProperties.current.ids)
      ).forEach(id => {
        const row = gridApiRef.current?.getRowNode?.(id);
        if (
          row &&
          row.type === "group" &&
          row.isAutoGenerated &&
          row.groupingKey &&
          !row.childrenExpanded
        ) {
          gridApiRef.current?.setRowChildrenExpansion?.(id, true);
        }
      });

      setSelectedProperties(rowSelectionModel);
    },
    [gridApiRef, latestSelectedProperties]
  );

  React.useEffect(() => {
    // on filter change remove all selections
    const groupIds = (gridApiRef.current?.getAllRowIds?.() ?? []).filter(id => {
      const row = gridApiRef.current?.getRowNode?.(id);
      return Boolean(
        row && row.type === "group" && row.isAutoGenerated && row.groupingKey
      );
    });
    gridApiRef.current?.selectRows?.(groupIds, false, true);
  }, [gridApiRef, filteredRows]);

  const columns = React.useMemo(
    (): GridColDef<PropertyGridValue>[] => [
      {
        field: "key",
        headerName: t("Key", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return highlightWithMatches(property.key, property.match);
        },
      },
      {
        field: "label",
        headerName: t("Label", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <NoPropagateBox sx={{ display: "flex", flex: 1 }}>
              <TextField
                size="extra-small"
                name={`[${getKey(property)}].label`}
                label=""
                placeholder={t("Label", { ns: "Global" })}
                disabled={property.deleted || editMode === "bulk"}
              />
            </NoPropagateBox>
          );
        },
      },
      {
        field: "type",
        headerName: t("Type", {
          ns: "QuoteItem",
        }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return capitalize(property.type);
        },
      },
      {
        field: "allowMultiple",
        headerName: t("Multiple", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 0.5,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return property.isArray === true || property.isArray === false
            ? getBoolLabel(property.isArray)
            : "";
        },
      },
      {
        field: "unit",
        headerName: t("Unit", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return property.isNumber ? (
            <NoPropagateBox sx={{ display: "flex", flex: 1 }}>
              <AutocompleteField
                size="extra-small"
                name={`[${getKey(property)}].unit`}
                inputLabel=""
                inputPlaceholder={t("Unit", { ns: "QuoteItem" })}
                options={unitsOptions}
                getOptionLabel={option => option.value}
                sx={{ height: "28px" }}
                disabled={property.deleted || editMode === "bulk"}
              />
            </NoPropagateBox>
          ) : null;
        },
      },
      ...(docType === "TEMPLATE" &&
      viewer.organisation.capabilities.includes("TEMPLATE_SHARING")
        ? ([
            {
              field: "customizable",
              headerName: t("Customisable", { ns: "QuoteItem" }),
              sortable: false,
              groupable: false,
              flex: 1,
              renderCell({ row: property, rowNode }) {
                if (rowNode.type === "group") return null;
                return !property.isComputed ? (
                  <CheckboxField
                    name={`[${getKey(property)}].customizable`}
                    disabled={property.deleted || editMode === "bulk"}
                  />
                ) : null;
              },
            },
          ] as GridColDef<PropertyGridValue>[])
        : []),
      {
        field: "allowedValues",
        headerName: t("Allowed values", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return !property.isComputed ? (
            <AllowedValues
              values={property}
              disabled={property.deleted || editMode === "bulk"}
            />
          ) : null;
        },
      },
      {
        field: "prompt",
        headerName: t("Prompt", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <NoPropagateBox sx={{ display: "flex", flex: 1 }}>
              <TextField
                size="extra-small"
                name={`[${getKey(property)}].prompt`}
                label=""
                placeholder={t("Prompt", { ns: "QuoteItem" })}
                disabled={property.deleted || editMode === "bulk"}
              />
            </NoPropagateBox>
          );
        },
      },
      {
        field: "questionControl",
        headerName: t("Question control", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 2,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return !property.isComputed ? (
            <QuestionControl
              values={property}
              disabled={property.deleted || editMode === "bulk"}
            />
          ) : null;
        },
      },
      {
        field: "group",
        headerName: t("Group", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        // hideable: true,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <NoPropagateBox sx={{ display: "flex", flex: 1 }}>
              <TextField
                size="extra-small"
                name={`[${getKey(property)}].group`}
                label=""
                placeholder={t("Group", { ns: "QuoteItem" })}
                disabled={property.deleted || editMode === "bulk"}
              />
            </NoPropagateBox>
          );
        },
      },
      {
        field: "askIfExpr",
        headerName: t("Ask if", { ns: "QuoteItem" }),
        sortable: false,
        groupable: false,
        flex: 0,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return !property.isComputed && !property.created ? (
            <AskIfConditionExpressionIconField
              name={`[${getKey(property)}].askIfExpr`}
              projectId={projectId}
              docId={docId}
              itemId={property.item.id}
              currentItemId={property.item.id}
              currentPropertyKey={property.key}
              disabled={property.deleted || editMode === "bulk"}
            />
          ) : null;
        },
      },
      {
        field: "value",
        headerName: t("Value", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 1,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <NoPropagateBox sx={{ display: "flex", flex: 1 }}>
              <PropertyValue
                values={property}
                disabled={property.deleted || editMode === "bulk"}
                setPropertyValue={setPropertyValue}
                projectId={projectId}
                docId={docId}
                document={document}
              />
            </NoPropagateBox>
          );
        },
      },
      {
        field: "visibility",
        headerName: t("Visibility", { ns: "Global" }),
        sortable: false,
        groupable: false,
        flex: 0,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          return (
            <VisibilityIconButton
              name={`[${getKey(property)}].clientVisibility`}
              disabled={property.deleted || editMode === "bulk"}
            />
          );
        },
      },
      {
        field: "delete",
        headerName: "",
        sortable: false,
        groupable: false,
        flex: 0,
        minWidth: 34,
        maxWidth: 34,
        renderCell({ row: property, rowNode }) {
          if (rowNode.type === "group") return null;
          if (property.templateTypePropValues) return null;
          return editMode === "bulk" ? null : (
            <DeleteAndRestoreIconButton property={property} setRows={setRows} />
          );
        },
      },
    ],
    [
      t,
      docType,
      viewer.organisation.capabilities,
      editMode,
      getBoolLabel,
      unitsOptions,
      projectId,
      docId,
      setPropertyValue,
      document,
    ]
  );

  const itemsByIds = React.useMemo(
    (): Record<string, ManagePropertiesTable_ItemFragment> =>
      items.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}),
    [items]
  );

  const groupingColDef =
    React.useMemo((): GridGroupingColDefOverride<PropertyGridValue> => {
      return {
        headerName: t("Item", { ns: "Global" }),
        flex: 2,
        minWidth: 120,
        renderCell: params => {
          if (params.rowNode.type === "group") {
            const item = itemsByIds[params.value];
            assert(item, `No item for group: ${params.value}`);
            return (
              <GridTreeDataGroupingCell
                {...params}
                hideDescendantCount
                rowNode={{
                  ...params.rowNode,
                  groupingKey: params.rowNode.groupingKey,
                }}
                formattedValue={formatItem(item)}
              />
            );
          }
          return null;
        },
      } as GridGroupingColDefOverride<PropertyGridValue>;
    }, [itemsByIds, t]);

  const validationSchema = React.useMemo(
    () =>
      Yup.array().of(
        Yup.object().shape({
          label: Yup.string()
            .required()
            .min(1)
            .label(t("Name", { ns: "Global" })),
          type: Yup.string().oneOf(PROP_VALUES).required(),
          essential: Yup.boolean().required(),
          customizable: Yup.boolean().required(),
          unit: Yup.object().shape({ key: Yup.string().required() }).nullable(),
          group: Yup.string(),
        })
      ),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const handleSubmit = async (values: FormValues) => {
    const itemEntries = Object.entries(groupBy(rows, r => r.item.id))
      .map(([itemId, rows]) => {
        const properties = items.find(i => i.id === itemId)?.props2 ?? [];
        const toDelete = rows
          .filter(r => r.deleted && !r.created)
          .map(r => r.key);
        const toCreate = rows
          .filter(r => !r.deleted && r.created)
          .map(r => {
            const formValues = values[getKey(r)];
            assert(formValues, `No values found for key ${getKey(r)}`);
            return createPropertyEntryOnAdd(formValues);
          });
        const toModify = rows
          .filter(r => !r.deleted && !r.created)
          .filter(r => {
            const previousValues = initialValues[getKey(r)];
            const newValues = values[getKey(r)];
            assert(
              previousValues,
              `No previous values found for key ${getKey(r)}`
            );
            assert(newValues, `No new values found for key ${getKey(r)}`);
            // keep only values that were changed
            return !isEqualFormValues(previousValues, newValues);
          })
          .map(r => {
            const formValues = values[getKey(r)];
            assert(formValues, `No values found for key ${getKey(r)}`);
            const property = properties.find(p => p.key === r.key);
            assert(
              property,
              `Property with key ${r.key} not found for item ${itemId}`
            );
            return createPropertyEntryOnEdit(formValues, property);
          });
        return {
          itemId,
          create: toCreate,
          modify: toModify,
          delete: toDelete,
        };
      })
      .filter(
        ie =>
          ie.create.length > 0 || ie.modify.length > 0 || ie.delete.length > 0
      );

    // update rows at first to not render incorrect values
    setRows(rows => rows.filter(r => !r.deleted));

    try {
      await defineItemProps2Bulk({
        variables: {
          input: {
            projectId,
            docId,
            itemEntries,
          },
        },
      });
      enqueueSnackbar(t("Changes saved", { ns: "Global" }));
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const localeText = React.useMemo(
    () => ({
      footerTotalRows: "",
      footerTotalVisibleRows: (visibleCount: number, totalCount: number) =>
        `${t("Items", { ns: "QuoteItem" })}: ${visibleCount}, ${t("Properties", { ns: "QuoteItem" })}: ${totalCount}`,
    }),
    [t]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnMount={false}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        dirty,
        handleReset,
        setFieldValue,
        setValues,
      }) => (
        <Page
          {...pageProps}
          action={
            <>
              {editMode === "bulk" && (
                <>
                  <BulkEditAction
                    selectedProperties={selectedProperties}
                    docType={docType}
                    disabled={selectedProperties.ids.size === 0}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
              <ManualSave
                onCancel={handleReset}
                disabled={isSubmitting}
                disabledCancel={!dirty}
                form={formId}
                stackProps={{
                  sx: { maxWidth: `${theme.layout.formSubmitWidth.md}px` },
                }}
                buttonSize="medium"
              />
            </>
          }
        >
          <PageContainer>
            {topContent}
            <Form
              id={formId}
              style={{
                display: "flex",
                flex: 1,
                overflow: "auto",
              }}
            >
              <Stack
                direction="column"
                spacing={2}
                sx={[
                  {
                    width: "100%",
                  },
                  theme => ({
                    [`.${gridClasses.cell}.deleted`]: {
                      backgroundColor: theme.palette.grey["200"],
                      textDecoration: "line-through",
                    },
                  }),
                ]}
              >
                <ListHeader
                  ActionButtons={
                    <>
                      <SwitchEditModeButton
                        editMode={editMode}
                        onEditModeChange={setEditMode}
                      />
                      <ModalOpenButton
                        Modal={ItemPropertyAddModalWithItemSelect}
                        modalProps={{
                          items,
                          initialItemId:
                            filters.itemIds.length === 1
                              ? filters.itemIds.at(0)
                              : undefined,
                          docType,
                          projectId,
                          docId,
                          async handleComplete(property, formValues) {
                            assert(formValues.itemId, "No item id provided");
                            const item = items.find(
                              i => i.id === formValues.itemId
                            );
                            assert(item, "No item found");

                            if (
                              item.props2.some(
                                prop =>
                                  prop.key === getPropInputEntryKey(property)
                              )
                            ) {
                              throw new Error(
                                t(
                                  "A property already exists with this name. Please enter another name",
                                  { ns: "QuoteItem" }
                                )
                              );
                            }

                            const newValue: PropertyFormValue = {
                              ...formValues,
                              ...getPropValuesFromType(formValues),
                              label: formValues.key,
                              isComputed: false,
                              deleted: false,
                              item,
                            };

                            const newRow: PropertyGridValue = {
                              ...getPropValuesFromType(formValues),
                              key: formValues.key,
                              type: formValues.type,
                              isComputed: false,
                              item: { id: item.id },
                              prop: null,
                              created: true,
                              deleted: false,
                            };

                            await setFieldValue(
                              `[${getKey(newRow)}]`,
                              newValue
                            );
                            setRows(rows => [...rows, newRow]);
                          },
                        }}
                      >
                        <ButtonCreate
                          title={t("Add property", { ns: "QuoteItem" })}
                          sx={{ flexShrink: 0, flexGrow: 0 }}
                        />
                      </ModalOpenButton>
                    </>
                  }
                  SearchField={
                    <DebouncedSearchInput
                      defaultValue={filters.searchTerm}
                      placeholder={t("Search", { ns: "Global" })}
                      onChangeSearchTerm={newSearchTerm => {
                        setFilters(state => ({
                          ...state,
                          searchTerm: newSearchTerm,
                        }));
                      }}
                      autoFocus
                      style={{
                        flexGrow: 1,
                        maxWidth: `${theme.layout.searchFieldWidth.xs}px`,
                      }}
                    />
                  }
                  QuickFilter={
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{
                        flexWrap: "wrap",
                        alignItems: "center",
                        minHeight: "32px",
                      }}
                    >
                      <ButtonSelectItemsMultiple
                        items={items}
                        itemIds={filters.itemIds}
                        onChange={itemIds =>
                          setFilters(f => ({ ...f, itemIds }))
                        }
                      />
                    </Stack>
                  }
                  FilterChips={
                    canShowChips(filters) ? (
                      <FilterChips
                        items={items}
                        filters={filters}
                        setFilters={setFilters}
                        onReset={resetFilters}
                      />
                    ) : undefined
                  }
                />
                <DataGrid<PropertyGridValue>
                  apiRef={gridApiRef}
                  stateStore={stateStore}
                  columns={columns}
                  rows={filteredRows}
                  rowCount={filteredRows.length}
                  getRowId={getRowId}
                  paginationMode="server"
                  hideFooterPagination={true}
                  sortingMode="client"
                  checkboxSelection={editMode === "bulk"}
                  disableRowSelectionOnClick
                  rowSelectionModel={
                    editMode === "bulk"
                      ? selectedProperties
                      : defaultSelectionModel
                  }
                  onRowSelectionModelChange={onRowSelectionModelChange}
                  disableRowSelectionExcludeModel
                  autoHeight={false}
                  disableVirtualization={false}
                  virtualizeColumnsWithAutoRowHeight={true}
                  // tree data
                  treeData
                  getTreeDataPath={getTreeDataPath}
                  groupingColDef={groupingColDef}
                  getCellClassName={getCellClassName}
                  rowSelectionPropagation={rowSelectionPropagation}
                  // Virtualization optimizations
                  pagination={false}
                  getRowHeight={getRowHeight}
                  rowBufferPx={150}
                  columnBufferPx={40}
                  // group expansion
                  isGroupExpandedByDefault={isGroupExpandedByDefault}
                  onStateChange={onStateChange}
                  localeText={localeText}
                />
              </Stack>
            </Form>
          </PageContainer>
        </Page>
      )}
    </Formik>
  );
};

const defaultSelectionModel: GridRowSelectionModel = {
  type: "include",
  ids: new Set(),
};

const getRowId = (property: PropertyGridValue): GridRowId => getKey(property);
const getTreeDataPath = (property: PropertyGridValue): string[] => [
  property.item.id,
  property.key,
];
const isGroupExpandedByDefault = () => false;
const getRowHeight = () => 36;
const getCellClassName = (params: GridCellParams<PropertyGridValue>) => {
  if (isAutogeneratedRow(params.row)) return "";
  return `${params.row.deleted ? "deleted" : ""}`;
};
const rowSelectionPropagation: GridRowSelectionPropagation = {
  descendants: true,
  parents: true,
};

function DeleteAndRestoreIconButton({
  property,
  setRows,
}: {
  property: PropertyGridValue;
  setRows: React.Dispatch<React.SetStateAction<PropertyGridValue[]>>;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const formikContext = useFormikContext<FormValues>();

  return !property.deleted ? (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t("Are you sure you want to delete this property?", {
          ns: "QuoteItem",
        }),
        async handleConfirm() {
          setRows(rows =>
            rows.map(r =>
              getKey(r) === getKey(property) ? { ...r, deleted: true } : r
            )
          );
          await formikContext.setFieldValue(`[${getKey(property)}]`, {
            ...formikContext.values[getKey(property)],
            deleted: true,
          });
        },
      }}
    >
      <IconButton size="small" color="primary" sx={{ marginLeft: "-10px" }}>
        <Tooltip title={t("Delete", { ns: "Global" })}>
          <DeleteIcon fontSize="small" />
        </Tooltip>
      </IconButton>
    </ModalOpenButton>
  ) : (
    <IconButton
      size="small"
      color="primary"
      sx={{ marginLeft: "-10px" }}
      onClick={async () => {
        setRows(rows =>
          rows.map(r =>
            getKey(r) === getKey(property) ? { ...r, deleted: false } : r
          )
        );
        await formikContext.setFieldValue(`[${getKey(property)}]`, {
          ...formikContext.values[getKey(property)],
          deleted: false,
        });
      }}
    >
      <Tooltip title={t("Restore", { ns: "QuoteItem" })}>
        <RestoreIcon fontSize="small" />
      </Tooltip>
    </IconButton>
  );
}

function VisibilityIconButton({
  name,
  disabled,
}: {
  name: string;
  disabled?: boolean;
}) {
  const [{ value }, { touched, error }, { setValue }] = useField<boolean>(name);
  return (
    <IconButton
      size="small"
      onClick={async () => {
        await setValue(!value);
      }}
      disabled={disabled}
    >
      <VisibilityIndicatorIcon
        isVisible={value}
        fontSize="small"
        color="primary"
      />
    </IconButton>
  );
}

function QuestionControlIconButton({
  propertyKey,
  property,
  values,
  setFieldValue,
}: {
  propertyKey: string;
  property: PropertyFormValue;
  values: PropertyGridValue;
  setFieldValue: FormikHelpers<FormValues>["setFieldValue"];
}) {
  return (
    <ModalOpenButton
      Modal={QuestionControlModal}
      modalProps={{
        values: property,
        handleComplete: async values => {
          await setFieldValue(`[${propertyKey}]`, {
            ...property,
            ...values,
          });
        },
      }}
    >
      <IconButton size="small">
        <EditIcon fontSize="small" color="secondary" />
      </IconButton>
    </ModalOpenButton>
  );
}

const QuestionControlDisplay = React.memo(
  ({
    propertyKey,
    property,
    values,
    setFieldValue,
    disabled,
  }: {
    propertyKey: string;
    property: PropertyFormValue;
    values: PropertyGridValue;
    setFieldValue: FormikHelpers<FormValues>["setFieldValue"];
    disabled?: boolean;
  }) => {
    const { askWhomLabels, askWhenLabels } = useQuestionContol();
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        {!disabled && (
          <QuestionControlIconButton
            property={property}
            propertyKey={propertyKey}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
        <Typography variant="body2">
          {getQuestionControlLabel(property.questionControl, {
            askWhenLabels,
            askWhomLabels,
          })}
        </Typography>
      </Stack>
    );
  }
);

function QuestionControl({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
}) {
  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];
  assert(property, `No property found for key: ${propertyKey}`);
  return (
    <QuestionControlDisplay
      propertyKey={propertyKey}
      property={property}
      values={values}
      setFieldValue={formikContext.setFieldValue}
      disabled={disabled}
    />
  );
}

function AllowedValuesIconButton({
  propertyKey,
  property,
  values,
  setFieldValue,
}: {
  propertyKey: string;
  property: PropertyFormValue;
  values: PropertyGridValue;
  setFieldValue: FormikHelpers<FormValues>["setFieldValue"];
}) {
  return values.templateTypePropValues ? (
    <ModalOpenButton
      Modal={PropertyFromTemplateTypeAllowedValuesModal}
      modalProps={{
        values: property,
        handleComplete: async values => {
          await setFieldValue(`[${propertyKey}]`, {
            ...property,
            ...values,
          });
        },
      }}
    >
      <IconButton size="small">
        <EditIcon fontSize="small" color="secondary" />
      </IconButton>
    </ModalOpenButton>
  ) : (
    <ModalOpenButton
      Modal={PropertyAllowedValuesModal}
      modalProps={{
        values: property,
        handleComplete: async values => {
          await setFieldValue(`[${propertyKey}]`, {
            ...property,
            ...values,
          });
        },
      }}
    >
      <IconButton size="small">
        <EditIcon fontSize="small" color="secondary" />
      </IconButton>
    </ModalOpenButton>
  );
}

const AllowedValuesDisplay = React.memo(
  ({
    propertyKey,
    property,
    values,
    setFieldValue,
    disabled,
  }: {
    propertyKey: string;
    property: PropertyFormValue;
    values: PropertyGridValue;
    setFieldValue: FormikHelpers<FormValues>["setFieldValue"];
    disabled?: boolean;
  }) => {
    const { t } = useTranslate(["Global", "QuoteItem"]);
    const { getBoolLabel, getNumberLabel } = usePropertyHelpers();
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        {!disabled && (
          <AllowedValuesIconButton
            propertyKey={propertyKey}
            property={property}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
        <Typography variant="body2">
          {renderAllowedValues(property, t, getNumberLabel, getBoolLabel)}
        </Typography>
      </Stack>
    );
  }
);

function AllowedValues({
  values,
  disabled,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
}) {
  const formikContext = useFormikContext<FormValues>();
  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];
  assert(property, `No property found for key: ${propertyKey}`);
  return (
    <AllowedValuesDisplay
      propertyKey={propertyKey}
      property={property}
      values={values}
      setFieldValue={formikContext.setFieldValue}
      disabled={disabled}
    />
  );
}

function renderAllowedValues(
  property: PropertyFormValue,
  t: TFunction<"Global">,
  getNumberLabel: (value: number, step?: number) => string,
  getBoolLabel: (value: boolean) => string
) {
  if (property.isComputed) return null;
  if (property.isNumber && isRangeDefined(property)) {
    const min = property.range?.min ?? null;
    const max = property.range?.max ?? null;
    const step = property.step ?? null;
    return [
      min !== null && max !== null
        ? `${t("Range", { ns: "Global" })}: ${getNumberLabel(min)}–${getNumberLabel(max)}`
        : min !== null
          ? `${t("Min", { ns: "Global" })}: ${getNumberLabel(min)}`
          : max !== null
            ? `${t("Max", { ns: "Global" })}: ${getNumberLabel(max)}`
            : null,
      step !== null ? `Step: ${step}` : null,
    ]
      .filter(notNull)
      .join(", ");
  }
  if (property.isBool) {
    return property.defineValuesMediaBool
      ? property.valuesMediaBool
          .filter(v => v.media)
          .map(v => getBoolLabel(v.value))
          .join(" / ")
      : null;
  }
  return property.defineAllowedValues
    ? property.isText
      ? property.allowedValues
          .filter(av => av.value !== null)
          .map(av => av.value as string)
          .join(", ")
      : property.isNumber
        ? property.allowedValues
            .filter(av => av.value !== null)
            .map(av => getNumberLabel(av.value as number))
            .join(", ")
        : null
    : null;
}

function canShowChips(filters: Filters): boolean {
  return Boolean(filters.itemIds.length);
}

function FilterChips({
  filters,
  setFilters,
  onReset,
  items,
}: {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  onReset?(): void | Promise<void>;
  items: ItemSelect_ItemFragment[];
}) {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  return canShowChips(filters) ? (
    <Stack
      direction={"row"}
      spacing={1}
      useFlexGap
      sx={{
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <FilterChipGroup
        label={""}
        values={filters.itemIds}
        getValueLabel={value => {
          const item = items.find(i => i.id === value);
          if (!item) return "";
          return formatItem(item);
        }}
        setValues={value =>
          setFilters(state => ({
            ...state,
            itemIds: value ?? [],
          }))
        }
      />
      {onReset && (
        <Typography variant="body2">
          <Link onClick={onReset} color="secondary" sx={{ cursor: "pointer" }}>
            {t("Clear all", { ns: "Global" })}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;
}

type BooleanKeys<T> = {
  [K in keyof T]: T[K] extends boolean ? K : never;
}[keyof T];

function checkBulkValue<T extends PropertyFormValue, K extends BooleanKeys<T>>(
  values: T[],
  field: K
): { isAllTrue: boolean; isAllFalse: boolean } {
  const fieldValues = values.map(value => value[field]);
  const isAllTrue = fieldValues.every(v => v === true);
  const isAllFalse = fieldValues.every(v => v === false);

  return { isAllTrue, isAllFalse };
}

function PropertyValue({
  values,
  disabled,
  setPropertyValue,
  projectId,
  docId,
  document,
}: {
  values: PropertyGridValue;
  disabled?: boolean;
  setPropertyValue: (
    itemId: string,
    property: EnterItemProps2ValueEntry
  ) => Promise<void>;
  projectId: string | null;
  docId: string;
  document: DocumentByType;
}) {
  const formikContext = useFormikContext<FormValues>();

  if (!values.prop) return null;

  const propertyKey = getKey(values);
  const property = formikContext.values[propertyKey];
  assert(property, `No property found for key: ${propertyKey}`);

  return (
    <PropertyField
      size="extra-small"
      disabled={disabled}
      projectId={projectId}
      docId={docId}
      itemId={property.item.id}
      property={values.prop}
      setPropertyValue={value => setPropertyValue(property.item.id, value)}
      renderExpressionButton={button => (
        <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
          capability="EXPRESSIONS"
          permissionByType={{
            QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
            REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
            TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
            BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
          }}
          document={document}
        >
          {button}
        </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
      )}
    />
  );
}

function BulkEditAction({
  selectedProperties,
  docType,
  disabled,
}: {
  selectedProperties: GridRowSelectionModel;
  docType: DocType;
  disabled: boolean;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const viewer = useUserData().currentUser!;

  const formikContext = useFormikContext<FormValues>();
  const selectedValues = React.useMemo(
    () =>
      Object.entries(formikContext.values)
        .filter(([key]) => selectedProperties.ids.has(key))
        .map(([key, value]) => value),
    [formikContext.values, selectedProperties.ids]
  );

  const selectedValuesKeys = React.useMemo(
    () => selectedValues.map(v => getKey(v)),
    [selectedValues]
  );

  const customizable = checkBulkValue(selectedValues, "customizable");
  const clientVisibility = checkBulkValue(selectedValues, "clientVisibility");

  const areAllGroupsSame =
    selectedValues.length > 0 &&
    selectedValues.every(v => v.group === selectedValues[0].group);
  const areAllQuestionControlsSame =
    selectedValues.length > 0 &&
    selectedValues.every(
      v =>
        v.questionControl === selectedValues[0].questionControl ||
        isEqual(v.questionControl, selectedValues[0].questionControl)
    );

  return (
    <>
      {docType === "TEMPLATE" && (
        <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
          <Button
            color="primary"
            variant="outlined"
            startIcon={
              <Box
                sx={{
                  height: 0,
                  width: 32,
                  ml: -1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  size="small"
                  checked={customizable.isAllTrue}
                  indeterminate={
                    !customizable.isAllTrue && !customizable.isAllFalse
                  }
                  disabled={disabled}
                />
              </Box>
            }
            onClick={async () => {
              const newValue = !customizable.isAllTrue ? true : false;
              await formikContext.setValues({
                ...formikContext.values,
                ...Object.fromEntries(
                  selectedValuesKeys.map(key => {
                    const v = formikContext.values[key];
                    return [key, { ...v, customizable: newValue }];
                  })
                ),
              });
            }}
            disabled={disabled}
          >
            {t("Customisable", { ns: "QuoteItem" })}
          </Button>
        </RestrictedByCapabilityWithDebug>
      )}
      <Button
        color="primary"
        variant="outlined"
        startIcon={
          <Box
            sx={{
              height: 0,
              width: 32,
              ml: -1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              size="small"
              checked={clientVisibility.isAllTrue}
              indeterminate={
                !clientVisibility.isAllTrue && !clientVisibility.isAllFalse
              }
              disabled={disabled}
            />
          </Box>
        }
        onClick={async () => {
          const newValue = !clientVisibility.isAllTrue ? true : false;
          await formikContext.setValues({
            ...formikContext.values,
            ...Object.fromEntries(
              selectedValuesKeys.map(key => {
                const v = formikContext.values[key];
                return [key, { ...v, clientVisibility: newValue }];
              })
            ),
          });
        }}
        disabled={disabled}
      >
        {t("Visibility", { ns: "Global" })}
      </Button>
      <ModalOpenButton
        Modal={GroupModal}
        modalProps={{
          values:
            selectedValues.length > 0 && areAllGroupsSame
              ? selectedValues[0]
              : defaultGroupValues,
          handleComplete: async values => {
            await formikContext.setValues({
              ...formikContext.values,
              ...Object.fromEntries(
                selectedValuesKeys.map(key => {
                  const v = formikContext.values[key];
                  return [key, { ...v, ...pick(values, "group") }];
                })
              ),
            });
          },
        }}
      >
        <Button color="primary" variant="outlined" disabled={disabled}>
          {t("Group", { ns: "QuoteItem" })}
        </Button>
      </ModalOpenButton>
      <ModalOpenButton
        Modal={QuestionControlModal}
        modalProps={{
          hidePrompt: true,
          values:
            selectedValues.length > 0 && areAllQuestionControlsSame
              ? selectedValues[0]
              : defaultQuestionControlValues,
          handleComplete: async values => {
            await formikContext.setValues({
              ...formikContext.values,
              ...Object.fromEntries(
                selectedValuesKeys.map(key => {
                  const v = formikContext.values[key];
                  return [key, { ...v, ...pick(values, "questionControl") }];
                })
              ),
            });
          },
        }}
      >
        <Button color="primary" variant="outlined" disabled={disabled}>
          {t("Question control", { ns: "QuoteItem" })}
        </Button>
      </ModalOpenButton>
    </>
  );
}
